import {
  FormEvent, ReactNode, useEffect, useState,
} from 'react';
import { useRouter } from 'next/router';
import {
  FormControl, InputLabel, OutlinedInput, CircularProgress,
} from '@mui/material';
import { useApplicationState } from 'lib/contexts/ApplicationState';
import { validateUsername } from 'lib/helpers';
import { onClickTwitterSignin } from 'features/SignIn/TwitterSignIn';
import { imageAvatarUrl } from 'lib/utils/imageUtils';
import { NetworkIcon } from 'elements/NetworkIcon';
import { NETWORKS, NETWORK_IDS } from 'lib/constants';
import { onClickTwitchSignIn } from 'features/SignIn/TwitchSignIn';
import { onClickTikTokSignin } from 'features/SignIn/TikTokSignIn';
import { onClickInstagramSignIn } from 'features/SignIn/InstagramSignIn';
import { AllNetworks, NetworkUser } from 'lib/types/users';
import { Help } from '@mui/icons-material';
import { MobileTooltip } from 'components/MobileTooltip';

interface Props {
  saveLabel: string;
  info?: string;
  onSave: () => void;
  onCancel?: () => void;
  onSkip?: () => void;
}

export const UsernameForm = ({
  saveLabel, info, onSave, onCancel, onSkip,
}: Props) => {
  const router = useRouter();
  const { api, currentUser } = useApplicationState();

  // TODO: Change the AllNetworks type to use NetworkAccount
  const [userOnNetworks, setUserOnNetworks] = useState<AllNetworks | undefined>();
  const [isCheckingNetworks, setIsCheckingNetworks] = useState(false);
  const [usernameIsAvailable, setUsernameIsAvailable] = useState(false);
  const [username, setUsername] = useState('');
  const [usernameErr, setUsernameErr] = useState<string | ReactNode>('');

  const [isSaving, setIsSaving] = useState(false);

  const saveUsername = async () => {
    if (!username || usernameErr) return;
    setIsSaving(true);
    try {
      await api.saveUsername(username);
      onSave();
    } catch (err: any) {
      const message = err?.message;
      setUsernameErr(message);
    }
    setIsSaving(false);
  };

  const onChangeUsernameField = (updatedUsername: string) => {
    setUsername(updatedUsername);
    setUserOnNetworks(undefined);
    setUsernameErr('');
    setUsernameIsAvailable(false);
  };

  const claimPersonalIGUsername = async (claimedUsername: string) => {
    try {
      setIsSaving(true);

      await api.claimPersonalIgUsername(claimedUsername);
      onSave();
      setIsSaving(false);
    } catch (err: any) {
      setUsernameErr(err.message);
    }
    setIsSaving(false);
  };

  const checkUsername = async () => {
    setUserOnNetworks(undefined);
    setUsernameErr('');
    setUsernameIsAvailable(false);

    if (!username) {
      setUsernameErr('Username/handle is required.');
      return;
    }
    const validate = validateUsername(username);
    if (validate !== '') {
      setUsernameErr(validate);
      return;
    }
    setIsCheckingNetworks(true);
    try {
      const options = await api.getUsernameAvailability(username);
      if (Object.values(options).some((i) => i.name)) {
        setUserOnNetworks(options);
      } else {
        setUsernameIsAvailable(true);
      }
      setIsCheckingNetworks(false);
    } catch (err: any) {
      if (err.message.match('already taken')) {
        const message = (
          <>
            <p className="mb-2">
              The username
              {' '}
              <span className="font-semibold">
                @
                {username}
              </span>
              {' '}
              is already taken.
            </p>
            <div className="text-black dark:text-white">
              Is
              {' '}
              <b>
                @
                {username}
              </b>
              {' '}
              your account? If so,
              {' '}
              <button type="button" className="underline" onClick={() => router.push('/signout')}>
                cancel this sign up
              </button>
              {' '}
              and sign in with your existing Gondola credentials.
              <div>Need some help? Please email support@gondola.cc.</div>
            </div>
          </>
        );
        setUsernameErr(message);
      } else {
        setUsernameErr('We had trouble checking that username. Please try again in a moment or try a different username.');
      }
      setIsCheckingNetworks(false);
      setUsernameIsAvailable(false);
    }
  };

  /**
   * Users can click on any Twitter, TikTok, or Instagram profile.
   */
  function userCanBeIncluded(u: NetworkUser) {
    if (!u.name) {
      return false;
    }
    if ([
      NETWORK_IDS.twitter,
      NETWORK_IDS.tiktok,
      NETWORK_IDS.instagram,
      NETWORK_IDS.twitch,
    ].includes(u.network)) {
      return true;
    }

    return false;
  }

  /** For now we only support Twitter, TikTok, and Instagram */
  const onUserNetworkClick = async (user: any) => {
    switch (user.network) {
    case NETWORK_IDS.twitter:
      onClickTwitterSignin({
        returnTo: router.asPath || '/signup',
        errorTo: router.asPath || '/signup',
        username: user.username,
      });
      break;

    case NETWORK_IDS.tiktok:
      onClickTikTokSignin({
        returnTo: router.asPath || '/signup',
        errorTo: router.asPath || '/signup',
        username: user.username,
      });
      break;

    case NETWORK_IDS.instagram:
      if (!user.isBusiness) {
        await claimPersonalIGUsername(user.username);
      } else {
        onClickInstagramSignIn({
          returnTo: router.asPath || '/signup',
          errorTo: router.asPath || '/signup',
          username: user.username,
        });
      }
      break;

    case NETWORK_IDS.twitch:
      onClickTwitchSignIn({
        returnTo: router.asPath || '/signup',
        errorTo: router.asPath || '/signup',
        username: user.username,
      });
      break;

    default:
      console.error(`Network ID (${user.network}) not supported.`);
    }
  };

  const submitForm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    saveUsername();
  };

  useEffect(() => {
    const networkErr = router.query?.networkErrMessage as string;
    const networkName = router.query?.networkName || 'platform' as string;
    if (networkErr) {
      if (networkErr.includes('Mismatched')) {
        const [, desired, profile] = networkErr.split(' ');
        setUsername(desired);
        if (networkName === 'Instagram') {
          setUsernameErr(`You are attempting to claim '${desired}' as your username, but that was not one of your connected business ${networkName} handles. To claim a username from Instagram, the account needs to be a business account connected to a Facebook page. Please correct your username and try again.`);
          return;
        }
        setUsernameErr(`You are attempting to claim '${desired}' as your username, but your ${networkName} handle is '${profile}'. Maybe you signed into the wrong ${networkName} account or entered a wrong username? Please correct your username and try again.`);
        return;
      }
      setUsernameErr(networkErr);
    }

    if (router.query.welcome) {
      setUsername(router.query.welcome as string);
    }
  }, [router.query]);

  const possibleNetworkUsers = Object.values(userOnNetworks || {})
    .filter((user: NetworkUser) => userCanBeIncluded(user)) as NetworkUser[];

  return (
    <div className="p-4 max-w-xl md:min-w-[480px]">
      <div className="mb-4">
        <div className="font-semibold text-lg">
          Your email:
          {' '}
          {currentUser?.email}
        </div>
      </div>
      {info && (
        <div className="p-2 mb-4 rounded-lg bg-paleyellow border border-yellow">
          {info}
        </div>
      )}
      <div className="flex gap-2 items-center mb-2">
        <h1 className="text-2xl font-semibold">Claim your username/handle</h1>
        <MobileTooltip title="If you have any issues claiming a username, please contact support@gondola.cc and we can help!" placement="top" arrow>
          <Help color="secondary" />
        </MobileTooltip>
      </div>
      <h2 className="mb-4">
        This will be your unique public profile URL.
      </h2>
      <form onSubmit={submitForm}>
        <div className="mb-4 w-full">
          <FormControl fullWidth>
            <InputLabel htmlFor="username">Username/Handle</InputLabel>
            <OutlinedInput
              id="username"
              type="text"
              label="Username/Handle"
              className="mb-4"
              inputProps={{
                maxLength: 29,
              }}
              onChange={(e) => onChangeUsernameField(e.target.value)}
              value={username}
              fullWidth
              error={!!usernameErr}
            />
            <button
              type="button"
              className={usernameIsAvailable ? 'btn-primary-outlined' : 'btn-primary'}
              onClick={checkUsername}
              disabled={!username || isCheckingNetworks}
            >
              {isCheckingNetworks ? (
                <>
                  <CircularProgress color="inherit" size="1rem" className="mr-4" />
                  Checking
                </>
              ) : 'Check availability'}
            </button>
          </FormControl>
        </div>

        {userOnNetworks
          && (
            <>
              <p className="w-full mb-4">
                If you own
                {' '}
                {possibleNetworkUsers?.length === 1 ? 'the account' : 'one of the accounts'}
                {' '}
                below, click the button to connect it and claim
                {' '}
                <b>
                  @
                  {username}
                </b>
                {' '}
                as your Gondola username.
              </p>

              <>
                {possibleNetworkUsers.map((user: NetworkUser) => (
                  <div key={user.network} className="border rounded-lg p-4 mx-auto flex justify-between items-center mb-4">
                    <div className="flex gap-4">
                      <img
                        className="rounded-full"
                        src={imageAvatarUrl(user.avatarUrl, 100)}
                        width={50}
                        alt={user.name}
                      />
                      <div>
                        <div>{user.name}</div>
                        <div className="font-semibold">
                          @
                          {user.username}
                        </div>
                      </div>
                    </div>
                    <div>
                      <button
                        onClick={(e) => { e.preventDefault(); onUserNetworkClick(user); }}
                        type="button"
                        className="btn-xs px-2 border hover:bg-hintgray hover:dark:bg-darkgray"
                      >
                        <NetworkIcon network={user.network} iconColor="color" />
                        <span className="ml-2">
                          Connect to
                          {' '}
                          {NETWORKS[user.network].title}
                        </span>
                      </button>
                    </div>
                  </div>
                ))}
              </>
            </>
          )}
        {usernameIsAvailable && !userOnNetworks && (
          <div>
            Good news!
            {' '}
            <span className="font-semibold">
              @
              {username}
            </span>
            {' '}
            is available.
            <div>
              Click
              {' '}
              {saveLabel}
              {' '}
              to claim it.
            </div>
          </div>
        )}

        <div className="min-h-[100px]">
          <div className="text-red">{usernameErr}</div>
        </div>
        <div className="text-right py-2">
          {onCancel && (
            <button
              type="button"
              className="btn-primary-outlined mr-2"
              onClick={onCancel}
            >
              Cancel
            </button>
          )}
          {onSkip && (
            <button
              type="button"
              className="btn-primary-outlined mr-2"
              onClick={onSkip}
            >
              Skip
            </button>
          )}
          <button
            type="submit"
            className="btn-primary"
            disabled={!username
              || !!userOnNetworks
              || !usernameIsAvailable
              || isSaving
              || isCheckingNetworks}
          >
            {saveLabel}
          </button>
        </div>
      </form>
    </div>
  );
};
